import React from "react";
// RestaurantSchedule import is no longer needed as we define a simpler type below
// import { RestaurantSchedule } from "../../../../../api/restaurantsApi";

// Utility function to format time (HH:MM:SS or HH:MM) to AM/PM
const formatTimeForDisplay = (
  timeString: string | null | undefined
): string => {
  if (!timeString) return "N/A";
  const [hours, minutes] = timeString.split(":");
  const hour24 = parseInt(hours, 10);
  const minute = parseInt(minutes, 10);

  // Convert 24-hour to 12-hour format without timezone conversion
  const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
  const ampm = hour24 >= 12 ? "PM" : "AM";

  return `${hour12}:${minute.toString().padStart(2, "0")} ${ampm}`;
};

// New simplified type for daily schedule
interface SimpleDailySchedule {
  openTime: string | null | undefined;
  closeTime: string | null | undefined;
  // isActive could be passed directly if determined by the parent, or derived here.
  // For now, we will derive it from openTime and closeTime presence.
}

interface RestaurantStatusProps {
  capacity: number;
  description: string;
  // Changed from dineInSchedule?: RestaurantSchedule and removed roomServiceSchedule
  schedule?: SimpleDailySchedule;
}

const RestaurantStatus: React.FC<RestaurantStatusProps> = ({
  capacity,
  description,
  schedule, // Changed from dineInSchedule, roomServiceSchedule
}) => {
  // Updated formatSchedule to use SimpleDailySchedule and remove middle times
  const formatScheduleDisplay = (
    currentSchedule: SimpleDailySchedule | undefined
  ) => {
    if (!currentSchedule) return "Not Available";
    if (!currentSchedule.openTime || !currentSchedule.closeTime)
      return "Not Available";
    const open = formatTimeForDisplay(currentSchedule.openTime);
    const close = formatTimeForDisplay(currentSchedule.closeTime);
    // Removed middleOpen and middleClose logic
    return `${open} - ${close}`;
  };

  const dailyHours = formatScheduleDisplay(schedule);
  // roomServiceHours removed

  // Determine overall status based on the single schedule provided
  // hasNoData: schedule is null/undefined (completely missing)
  const hasNoData = !schedule;
  // isOpen: schedule exists AND both times exist AND are not empty
  const isOpen = !!(schedule && schedule.openTime && schedule.closeTime);
  // Closed: schedule exists but doesn't have valid times (one or both times missing/null)
  // This is the case where schedule exists but times are null/undefined

  const statusText = isOpen
    ? "Open Today"
    : hasNoData
    ? "No Data Available"
    : "Closed Today";

  return (
    <div className="border border-gray-300 dark:border-gray-700 rounded-md p-4 w-72 bg-white dark:bg-gray-800">
      {/* Status Row */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center">
          <span
            className={`h-3 w-3 rounded-full ${
              isOpen ? "bg-green-500" : hasNoData ? "bg-gray-500" : "bg-red-500"
            } mr-2`}
          />
          <span
            className={`${
              isOpen
                ? "text-green-700"
                : hasNoData
                ? "text-gray-500"
                : "text-red-700"
            } font-medium dark:text-white`}
          >
            {statusText}
          </span>
        </div>
        {capacity > 0 && (
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Capacity: {capacity}
          </span>
        )}
      </div>

      {/* Restaurant Description */}
      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 mb-3">
        {description}
      </p>

      {/* Today's hours */}
      <div className="text-sm text-gray-600 dark:text-gray-400 mt-3">
        <p>
          <strong>Today's Hours:</strong>
        </p>
        {/* Display single schedule, could be labeled as Dine-in or general Operating Hours */}
        <p className="ml-2">Operating Hours: {dailyHours}</p>
        {/* Removed roomServiceHours display */}
        {/* <p className="ml-2">Room Service: {roomServiceHours}</p> */}
      </div>
    </div>
  );
};

export default RestaurantStatus;
